// (owner, Oct 2: "on mobile, the use button should show the dominating available action, like toolbox when standing next to a car if
// you have toolbox in your inventory") what F — the phone's ✚ — does where you stand, in the order Game.stepPlayer tries it: the picked
// lock, the repair, your gadget back into the pockets, the bench. Nothing here means the old eat / drink / buy. The tests are the game's
// own (repairTarget is the one stepRepair uses), so the button shows the action the press takes.
import type { Person, VehicleEntity } from './entities';
import type { WorldStream } from '../engine/world-stream';
import { vehicleMaxHp } from './shop';
import { fuelOf } from './phone';
import { canSling, isContainer, SLING_ALT, SLING_REACH, trailerHint } from './trailer';
import { vehiclePower } from '../content/types';

export interface UseHint { id: 'lockpick' | 'bell' | 'repair' | 'gadget' | 'sit' | 'stand' | 'fell' | 'garage' | 'trailer' | 'sling' | 'pump'; icon: string; word: string; /** the action runs while the button is held (the pump, the harvester's saw) */ hold?: boolean }

/** the fuel a spot sells: on a station's lot (where the pumps stand), or beside a station building (Game.stationAt) */
export function stationAt(world: WorldStream, x: number, y: number): 'gas' | 'electric' | undefined {
  for (const pad of world.parkingIn(x - 1, y - 1, x + 1, y + 1)) { if (x < pad.x - 0.5 || x > pad.x + pad.w + 0.5 || y < pad.y - 0.5 || y > pad.y + pad.h + 0.5) continue; const [bx, by] = pad.key.split(',').map(Number); const b = world.buildingAt(bx + 0.5, by + 0.5); const k = b ? fuelOf(b.def.tags) : undefined; if (k) return k; }
  for (const [dx, dy] of [[0, 0], [2, 0], [-2, 0], [0, 2], [0, -2]] as const) {
    const b = world.buildingAt(x + dx, y + dy);
    if (!b) continue;
    const t = b.def.tags;
    if (t.includes('charging') || t.includes('ev_charger') || t.includes('charger')) return 'electric';
    if (t.includes('gas_station') || t.includes('fuel') || t.includes('truck_stop') || t.includes('petrol')) return 'gas';
  }
  return undefined;
}

/** the damaged vehicle a toolbox would work on from here: standing empty on the ground, within reach (a wreck counts — F says it needs a tow) */
export function repairTarget(p: Person, vehicles: readonly VehicleEntity[]): VehicleEntity | undefined {
  let best: VehicleEntity | undefined, bd = 2.6, wreck: VehicleEntity | undefined;
  for (const v of vehicles) {
    if (v.driver || v.alt > 0.5 || v.def.class === 'train' || vehicleMaxHp(v) - v.hp < 1) continue;
    const d = Math.hypot(v.x - p.x, v.y - p.y) - Math.min(v.def.size[0], v.def.size[1]) / 2;
    if (d >= bd) continue;
    if (v.wrecked) { wreck = v; continue; }
    bd = d; best = v;
  }
  return best ?? wreck;
}
/** a seat (a bench) within reach — the tile test of Game.sitDown */
export function seatNear(world: WorldStream, p: Person): boolean {
  for (let dx = -1; dx <= 1; dx++) for (let dy = -1; dy <= 1; dy++) {
    const tx = Math.floor(p.x) + dx, ty = Math.floor(p.y) + dy;
    if (world.propAt(tx + 0.5, ty + 0.5)?.seat && Math.hypot(tx + 0.5 - p.x, ty + 0.5 - p.y) < 1.3) return true;
  }
  return false;
}

export function useHint(p: Person, vehicles: readonly VehicleEntity[], world: WorldStream, o: { lockedDoorNear: () => boolean; myGadget: () => 'cam' | 'sensor' | undefined; /** at the door of a garage of yours (the game knows; the browser does not) */ garageNear?: () => boolean; /** at a player's locked front door (a home, not a business) — no pick: F rings */ bellNear?: () => boolean }): UseHint | undefined {
  if (p.dead) return undefined;
  if (p.vehicle) return driverHint(p, p.vehicle, vehicles, world, o.garageNear);
  if (p.act === 'sit') return { id: 'stand', icon: '🧍', word: 'Stand up' };
  if (!p.inside && (p.items.lockpick_set ?? 0) > 0 && o.lockedDoorNear()) return { id: 'lockpick', icon: '🔓', word: 'Pick lock' };
  if (!p.inside && (p.items.lockpick_set ?? 0) <= 0 && o.bellNear?.()) return { id: 'bell', icon: '🔔', word: 'Ring' };
  if (!p.inside && (p.items.toolbox ?? 0) > 0) { const v = repairTarget(p, vehicles); if (v) return { id: 'repair', icon: '🧰', word: v.wrecked ? 'Wreck' : 'Repair' }; }
  const g = o.myGadget(); if (g) return { id: 'gadget', icon: g === 'cam' ? '📷' : '📡', word: 'Pick up' };
  if (!p.inside && seatNear(world, p)) return { id: 'sit', icon: '🪑', word: 'Sit' };
  return undefined;
}

/** at the wheel, in stepPlayer's order: the harvester's saw on a locked tree, your garage door, a trailer (hitch, unhitch, load, launch,
 * the tow truck's hook), the heavy-lift helicopter's sling, the pump. The car's own settings (⚙) are the button when none of these is */
function driverHint(p: Person, v: VehicleEntity, vehicles: readonly VehicleEntity[], world: WorldStream, garageNear?: () => boolean): UseHint | undefined {
  if (v.driver !== p || v.wrecked) return undefined;
  if (p.lockTree && v.def.tags.includes('harvester')) return { id: 'fell', icon: '🪚', word: 'Hold: fell', hold: true };
  if (garageNear?.()) return { id: 'garage', icon: '🅿️', word: 'Park below' };
  const th = trailerHint(vehicles as VehicleEntity[], world, v);
  if (th?.startsWith('F ')) { const w = th.slice(2); return { id: 'trailer', icon: /boat|launch|load/.test(w) ? '🚤' : '🔗', word: w[0].toUpperCase() + w.slice(1) }; }
  if (canSling(v)) {
    if (v.cargo !== undefined && v.alt <= SLING_ALT) return { id: 'sling', icon: '🏗️', word: 'Set down' };
    if (v.cargo === undefined && v.alt >= 1 && v.alt <= SLING_ALT && vehicles.some((c) => isContainer(c) && !c.wrecked && c.towedBy === undefined && c.onTrailer === undefined && c.alt <= 0.5 && Math.hypot(c.x - v.x, c.y - v.y) < SLING_REACH)) return { id: 'sling', icon: '🏗️', word: 'Lift' };
  }
  const want = vehiclePower(v.def);
  if (Math.abs(v.speed) < 0.5 && v.fuel < 1 && stationAt(world, v.x, v.y) === want) return want === 'electric' ? { id: 'pump', icon: '🔌', word: 'Hold: charge', hold: true } : { id: 'pump', icon: '⛽', word: 'Hold: fill', hold: true };
  return undefined;
}
